import React from 'react';

import './more-info-icon.scss';

import Icon from '../Icon/Icon';
import ConditionalLink from '../Link/ConditionalLink';
import type { TooltipAlignment, TooltipSide } from '../Tooltip/InternalFixedTooltipPlacement';
import Tooltip, { type TooltipProps } from '../Tooltip/Tooltip';

export interface MoreInfoIconProps {
  /**
   * Tooltip that appears upon hovering on the info icon. Even if there is an onClick,
   * there should be a tooltip describing what will happen if the user clicks on it.
   */
  tooltip: string | JSX.Element;

  /**
   * Secondary text that appears upon hovering on the info icon.
   */
  secondaryText?: TooltipProps['secondaryText'];

  /**
   * Link to redirect to.
   *
   * Relative links (determined by whether the input starts with "/") will open in the current tab.
   * Absolute links will open in a new tab by default.
   */
  link?: string;

  tooltipAlignment?: TooltipAlignment;

  /** Indicates whether to use the outline variant of the info icon or not. */
  outline?: boolean;
  /** Indicates whether the tooltip is an interactive tooltip or not. */
  interactive?: boolean;

  tooltipSide?: TooltipSide;
}

const MoreInfoIcon: React.FC<MoreInfoIconProps> = (props) => {
  const {
    interactive,
    tooltip,
    secondaryText,
    link,
    tooltipAlignment = 'start',
    tooltipSide = 'bottom',
    outline,
  } = props;

  return (
    <Tooltip
      tooltip={tooltip}
      secondaryText={secondaryText}
      placementType="fixed"
      alignment={tooltipAlignment}
      side={tooltipSide}
      interactive={interactive}
    >
      <ConditionalLink link={link}>
        <div
          className="more-info-icon"
          data-testid="more-info-icon"
          aria-label="More info"
          onClick={(ev): void => {
            ev.stopPropagation();
          }}
        >
          <Icon src={outline ? 'INFO_OUTLINE' : 'INFO'} />
        </div>
      </ConditionalLink>
    </Tooltip>
  );
};

export default MoreInfoIcon;
